import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'dialog',
    'dialogCaption',
    'dialogImage',
    'modalThumb',
    'pageThumb'
  ]

  connect() {
    this.activeIndex = 0
    this.isOpen = false
    this.items = this.pageThumbTargets.map((thumb) => ({
      alt: thumb.dataset.alt,
      url: thumb.dataset.url
    }))

    this.#render()
  }

  disconnect() {
    this.#unlockScroll()
  }

  open(event) {
    const index = this.#indexFromEvent(event)

    this.show(index)
  }

  close() {
    if (!this.hasDialogTarget) return

    this.isOpen = false
    this.dialogTarget.classList.add('hidden')
    this.dialogTarget.classList.remove('flex')
    this.dialogTarget.setAttribute('aria-hidden', 'true')
    this.#unlockScroll()
  }

  closeBackdrop(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  next() {
    if (this.items.length < 2) return

    this.show((this.activeIndex + 1) % this.items.length)
  }

  previous() {
    if (this.items.length < 2) return

    this.show(
      (this.activeIndex - 1 + this.items.length) %
        this.items.length
    )
  }

  select(event) {
    const index = this.#indexFromEvent(event)

    this.show(index)
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.next()
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.previous()
    }
  }

  show(index) {
    this.activeIndex = this.#clamp(index)
    this.#render()

    if (!this.hasDialogTarget) return

    this.isOpen = true
    this.dialogTarget.classList.remove('hidden')
    this.dialogTarget.classList.add('flex')
    this.dialogTarget.setAttribute('aria-hidden', 'false')
    this.#lockScroll()
  }

  #clamp(index) {
    if (this.items.length === 0) return 0
    if (index < 0) return 0
    if (index >= this.items.length) {
      return this.items.length - 1
    }

    return index
  }

  #indexFromEvent(event) {
    const rawIndex = event?.currentTarget?.dataset?.index

    if (rawIndex == null || rawIndex === '') {
      return this.activeIndex
    }

    return this.#clamp(parseInt(rawIndex, 10))
  }

  #lockScroll() {
    document.body.classList.add('overflow-hidden')
  }

  #render() {
    const item = this.items[this.activeIndex]

    if (!item) return

    if (this.hasDialogImageTarget) {
      this.dialogImageTarget.src = item.url
      this.dialogImageTarget.alt = item.alt
    }

    if (this.hasDialogCaptionTarget) {
      this.dialogCaptionTarget.textContent =
        `Image ${this.activeIndex + 1} of ${
          this.items.length
        }`
    }

    this.modalThumbTargets.forEach((thumb, index) => {
      const isActive = index === this.activeIndex

      thumb.classList.toggle('ring-brand-red', isActive)
      thumb.classList.toggle('ring-2', isActive)
      thumb.classList.toggle('shadow-sm', isActive)
      thumb.classList.toggle('opacity-60', !isActive)
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false')
    })
  }

  #unlockScroll() {
    document.body.classList.remove('overflow-hidden')
  }
}
